import { Button, Text, useToastContext } from "@ttc3k/trekker";
import { Flex } from "styled-system/jsx";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  ActivityLogManyDocument,
  PendingRenewalEmailsDocument,
  usePendingRenewalEmailsQuery,
  useMembershipCancelPendingRenewalEmailsMutation,
  useMembershipSendPendingRenewalEmailsNowMutation,
} from "gql/generated";

type PendingRenewalEmailsLineProps = {
  organizationId: string;
  operatorEntityId: string;
};

const kindLabelKey = (kind: string): string => {
  switch (kind) {
    case "REMINDER":
      return "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_REMINDER";
    case "EXPIRED":
      return "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_EXPIRED";
    case "OVERDUE":
      return "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_OVERDUE";
    case "CANCELLED":
      return "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_CANCELLED";
    default:
      return "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.KIND_SUCCESS";
  }
};

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

export const PendingRenewalEmailsLine = ({
  organizationId,
  operatorEntityId,
}: PendingRenewalEmailsLineProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { data } = usePendingRenewalEmailsQuery({
    variables: { organizationId, operatorEntityId },
    skip: !organizationId || !operatorEntityId,
    fetchPolicy: "cache-and-network",
  });

  const [cancelEmails, { loading: cancelling }] =
    useMembershipCancelPendingRenewalEmailsMutation();
  const [sendEmailsNow, { loading: sending }] =
    useMembershipSendPendingRenewalEmailsNowMutation();

  const pending = (data?.pendingRenewalEmails ?? []).filter(
    (e): e is NonNullable<typeof e> => !!e,
  );
  if (pending.length === 0) return null;

  const handleCancel = async (id: string) => {
    await cancelEmails({
      variables: { ids: [id] },
      refetchQueries: [PendingRenewalEmailsDocument],
    });
  };

  const handleSendNow = async (id: string) => {
    try {
      const { data: sendData } = await sendEmailsNow({
        variables: { ids: [id] },
        refetchQueries: [PendingRenewalEmailsDocument, ActivityLogManyDocument],
        awaitRefetchQueries: true,
      });
      toastFactory.create({
        title: t("core:SUCCESS"),
        description: t(
          "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SEND_NOW_SUCCESS",
          {
            count:
              sendData?.membershipSendPendingRenewalEmailsNow?.sent ?? 0,
          },
        ),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t(
          "directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SEND_NOW_ERROR",
        ),
      });
    }
  };

  return (
    <Flex flexDir="column" gap="100">
      {pending.map(email => (
        <Flex
          key={email._id}
          alignItems="center"
          justifyContent="space-between"
          gap="200"
          p="200"
          borderRadius="150"
          bg="bg.accent.yellow.light"
          borderWidth="1px"
          borderColor="border.accent.yellow"
        >
          <Text visual="smallRegular" color="text.dark">
            {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.PENDING_LINE", {
              kind: capitalize(t(kindLabelKey(email.emailKind))),
              time: format(
                new Date(email.scheduledSendAt),
                "MMM d, yyyy 'at' h:mm a",
              ),
            })}
          </Text>
          <Flex gap="100" flexShrink={0}>
            <Button
              mode="light"
              size="sm"
              disabled={cancelling || sending}
              isLoading={sending}
              onClick={() => handleSendNow(email._id)}
            >
              {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.SEND_NOW")}
            </Button>
            <Button
              mode="light"
              size="sm"
              visual="destructive"
              disabled={cancelling || sending}
              isLoading={cancelling}
              onClick={() => handleCancel(email._id)}
            >
              {t("directory:OPERATOR.BACK_OFFICE.RENEWAL_EMAIL.CANCEL")}
            </Button>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};
